import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Package, DollarSign, Hash, MapPin, User, Phone, Calendar } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { dataService } from '../../services/dataService';
import { useCategories } from '../../hooks/useCategories';

interface AddRawMaterialModalProps {
  onClose: () => void;
  onSuccess: () => void;
}

const AddRawMaterialModal: React.FC<AddRawMaterialModalProps> = ({ onClose, onSuccess }) => {
  const { tenantData } = useAuth();
  const { getCategoryNames } = useCategories('rawMaterial');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const categories = getCategoryNames();
  
  const [formData, setFormData] = useState({
    name: '',
    category: categories.length > 0 ? categories[0] : '',
    currentStock: 0,
    minStock: 0,
    maxStock: 0,
    
    // Main unit system (purchase/storage unit)
    mainUnit: 'box',
    mainUnitQuantity: 0,
    costPerMainUnit: 0,
    
    // Sub-unit system (smallest sellable unit)
    subUnit: 'piece',
    subUnitsPerMainUnit: 1,
    
    // Other fields
    supplier: '',
    supplierContact: '',
    location: '',
    description: '',
    expiryDate: '',
    
    // Advanced mode toggle
    useSubUnits: false
  });

  const mainUnits = ['box', 'carton', 'bag', 'bottle', 'can', 'pack', 'kg', 'lbs', 'liters', 'gallons'];
  const subUnits = ['piece', 'stick', 'bottle', 'can', 'gram', 'ml', 'unit', 'serving'];

  // Lock body scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  // Update category when categories load
  React.useEffect(() => {
    if (categories.length > 0 && !formData.category) {
      setFormData(prev => ({ ...prev, category: categories[0] }));
    }
  }, [categories, formData.category]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? parseFloat(value) || 0 : value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name.trim() || !formData.supplier.trim()) {
      setError('Please fill in all required fields');
      return;
    }

    if (formData.minStock >= formData.maxStock) {
      setError('Maximum stock must be greater than minimum stock');
      return;
    }

    try {
      setLoading(true);
      setError('');

      const materialData = {
        name: formData.name.trim(),
        category: formData.category.trim(),
        currentStock: parseFloat(formData.currentStock.toString()),
        minStock: parseFloat(formData.minStock.toString()),
        maxStock: parseFloat(formData.maxStock.toString()),
        
        // Main unit system (purchase/storage unit)
        mainUnit: formData.useSubUnits ? formData.mainUnit.trim() : formData.subUnit.trim(),
        mainUnitQuantity: formData.useSubUnits ? 
          Math.floor(parseFloat(formData.currentStock.toString()) / parseFloat(formData.subUnitsPerMainUnit.toString())) : 
          parseFloat(formData.currentStock.toString()),
        
        // Sub-unit system (smallest sellable unit)
        subUnit: formData.subUnit.trim(),
        subUnitsPerMainUnit: formData.useSubUnits ? parseFloat(formData.subUnitsPerMainUnit.toString()) : 1,
        
        // Cost information
        costPerMainUnit: parseFloat(formData.costPerMainUnit.toString()),
        costPerSubUnit: formData.useSubUnits ? 
          parseFloat(formData.costPerMainUnit.toString()) / parseFloat(formData.subUnitsPerMainUnit.toString()) :
          parseFloat(formData.costPerMainUnit.toString()),
        
        // Supplier and other info
        supplier: formData.supplier.trim(),
        supplierContact: formData.supplierContact.trim() || undefined,
        location: formData.location.trim() || undefined,
        description: formData.description.trim() || undefined,
        expiryDate: formData.expiryDate ? new Date(formData.expiryDate) : undefined,
        
        // Legacy fields for backward compatibility
        baseUnit: formData.subUnit.trim(),
        costPerUnit: formData.useSubUnits ? 
          parseFloat(formData.costPerMainUnit.toString()) / parseFloat(formData.subUnitsPerMainUnit.toString()) :
          parseFloat(formData.costPerMainUnit.toString()),
        unitConversions: formData.useSubUnits ? [
          {
            unitName: formData.mainUnit.trim(),
            conversionFactor: parseFloat(formData.subUnitsPerMainUnit.toString()),
            isPurchaseUnit: true,
            isSaleUnit: true,
            costPerConversionUnit: parseFloat(formData.costPerMainUnit.toString())
          }
        ] : [],
        defaultPurchaseUnit: formData.useSubUnits ? formData.mainUnit.trim() : formData.subUnit.trim(),
        defaultSaleUnit: formData.subUnit.trim()
      };

      await dataService.createRawMaterial(tenantData!.id, materialData);
      onSuccess();
    } catch (error) {
      console.error('Error creating raw material:', error);
      setError('Failed to create raw material. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const modalContent = (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[9999]">
      <div className="bg-white/95 dark:bg-gray-800/95 backdrop-blur-md rounded-lg shadow-xl w-full max-w-4xl h-full md:h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-xl tablet:text-2xl font-bold text-gray-900 dark:text-white">
            Add Raw Material
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
              <p className="text-red-700 dark:text-red-300">{error}</p>
            </div>
          )}

          {/* Basic Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Basic Information</h3>
            
            <div className="grid grid-cols-1 tablet:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Material Name *
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Package className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                  </div>
                  <input
                    type="text"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleInputChange}
                    className="input-field pl-10"
                    placeholder="e.g., Coffee Beans - Arabica"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Category *
                </label>
                <select
                  name="category"
                  value={formData.category}
                  onChange={handleInputChange}
                  className="input-field"
                >
                  {categories.map(category => (
                    <option key={category} value={category}>
                      {category}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Description
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                rows={3}
                className="input-field"
                placeholder="Optional description of the material"
              />
            </div>
          </div>

          {/* Stock Information */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Stock Information</h3>
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  name="useSubUnits"
                  checked={formData.useSubUnits}
                  onChange={handleInputChange}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 dark:border-gray-600 rounded"
                />
                <label className="text-sm text-gray-700 dark:text-gray-300">
                  Use sub-units (e.g., box → pieces)
                </label>
              </div>
            </div>
            
            {formData.useSubUnits && (
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-4">
                <div className="flex items-center space-x-2 mb-2">
                  <Package className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                  <h4 className="font-medium text-blue-900 dark:text-blue-100">Sub-Unit Configuration</h4>
                </div>
                <p className="text-sm text-blue-700 dark:text-blue-300 mb-4">
                  Set up main units (how you buy/store) and sub-units (how you sell). 
                  Example: 1 box = 20 pieces
                </p>
                
                <div className="grid grid-cols-1 tablet:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Main Unit *
                    </label>
                    <select
                      name="mainUnit"
                      value={formData.mainUnit}
                      onChange={handleInputChange}
                      className="input-field"
                    >
                      {mainUnits.map(unit => (
                        <option key={unit} value={unit}>
                          {unit}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Sub-Unit *
                    </label>
                    <select
                      name="subUnit"
                      value={formData.subUnit}
                      onChange={handleInputChange}
                      className="input-field"
                    >
                      {subUnits.map(unit => (
                        <option key={unit} value={unit}>
                          {unit}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {formData.subUnit}s per {formData.mainUnit} *
                    </label>
                    <input
                      type="number"
                      name="subUnitsPerMainUnit"
                      required
                      min="1"
                      step="1"
                      value={formData.subUnitsPerMainUnit}
                      onChange={handleInputChange}
                      className="input-field"
                      placeholder="e.g., 20"
                    />
                  </div>
                </div>
                
                <div className="mt-3 p-3 bg-white/90 dark:bg-gray-800/90 backdrop-blur-md rounded border border-gray-200 dark:border-gray-600">
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    <strong>Example:</strong> 1 {formData.mainUnit} = {formData.subUnitsPerMainUnit} {formData.subUnit}s
                  </p>
                </div>
              </div>
            )}
            
            <div className="grid grid-cols-1 tablet:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Current Stock ({formData.useSubUnits ? formData.subUnit : formData.mainUnit}s) *
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Hash className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                  </div>
                  <input
                    type="number"
                    name="currentStock"
                    required
                    min="0"
                    step="0.01"
                    value={formData.currentStock}
                    onChange={handleInputChange}
                    className="input-field pl-10"
                    placeholder="0"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Minimum Stock ({formData.useSubUnits ? formData.subUnit : formData.mainUnit}s) *
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Hash className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                  </div>
                  <input
                    type="number"
                    name="minStock"
                    required
                    min="0"
                    step="0.01"
                    value={formData.minStock}
                    onChange={handleInputChange}
                    className="input-field pl-10"
                    placeholder="0"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Maximum Stock ({formData.useSubUnits ? formData.subUnit : formData.mainUnit}s) *
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Hash className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                  </div>
                  <input
                    type="number"
                    name="maxStock"
                    required
                    min="0"
                    step="0.01"
                    value={formData.maxStock}
                    onChange={handleInputChange}
                    className="input-field pl-10"
                    placeholder="0"
                  />
                </div>
              </div>

              {formData.useSubUnits && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Cost per {formData.mainUnit} *
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <DollarSign className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                    </div>
                    <input
                      type="number"
                      name="costPerMainUnit"
                      required
                      min="0"
                      step="0.01"
                      value={formData.costPerMainUnit}
                      onChange={handleInputChange}
                      className="input-field pl-10"
                      placeholder="0.00"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Supplier Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Supplier Information</h3>
            
            <div className="grid grid-cols-1 tablet:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Supplier Name *
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <User className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                  </div>
                  <input
                    type="text"
                    name="supplier"
                    required
                    value={formData.supplier}
                    onChange={handleInputChange}
                    className="input-field pl-10"
                    placeholder="Supplier name"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Supplier Contact
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Phone className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                  </div>
                  <input
                    type="text"
                    name="supplierContact"
                    value={formData.supplierContact}
                    onChange={handleInputChange}
                    className="input-field pl-10"
                    placeholder="Phone or email"
                  />
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Storage Location
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <MapPin className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                </div>
                <input
                  type="text"
                  name="location"
                  value={formData.location}
                  onChange={handleInputChange}
                  className="input-field pl-10"
                  placeholder="e.g., Warehouse A, Shelf 3"
                />
              </div>
            </div>
          </div>

          {/* Additional Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Additional Information</h3>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Expiry Date
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Calendar className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                </div>
                <input
                  type="date"
                  name="expiryDate"
                  value={formData.expiryDate}
                  onChange={handleInputChange}
                  className="input-field pl-10"
                />
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200 dark:border-gray-700">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? 'Creating...' : 'Create Material'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  // Use portal to render at document body level
  return createPortal(modalContent, document.body);
};

export default AddRawMaterialModal; 